import { Option } from "@siteimprove/alfa-option";
import { Parser } from "@siteimprove/alfa-parser";
import { Function as CSSFunction } from "../../syntax/index.js";
import { List } from "../collection/index.js";
import { Number, Percentage } from "../numeric/index.js";
import { Function } from "./function.js";
const { map, either } = Parser;
/**
 * Class representing a transformation that resizes an element in either 2D or 3D space.
 * Is parsed from either a scale() or scale3d() transform function or a value of the scale property.
 *
 * @public
 */
export class Scale extends Function {
    static of(x, y, z) {
        return new Scale(x.resolve(), y.resolve(), z?.resolve());
    }
    _x;
    _y;
    _z;
    constructor(x, y, z) {
        super("scale", false);
        this._x = x;
        this._y = y;
        this._z = Option.from(z);
    }
    get kind() {
        return "scale";
    }
    get x() {
        return this._x;
    }
    get y() {
        return this._y;
    }
    get z() {
        return this._z;
    }
    resolve() {
        return this;
    }
    equals(value) {
        return (value instanceof Scale &&
            value._x.equals(this._x) &&
            value._y.equals(this._y) &&
            value._z.equals(this._z));
    }
    hash(hash) {
        hash.writeHashable(this._x).writeHashable(this._y).writeHashable(this._z);
    }
    toJSON() {
        return {
            ...super.toJSON(),
            x: this._x.toJSON(),
            y: this._y.toJSON(),
            ...(this._z.isSome() ? { z: this._z.get().toJSON() } : {}),
        };
    }
    toString() {
        if (this._z.isSome()) {
            return `scale3d(${this._x}, ${this._y}, ${this._z})`;
        }
        if (this._x.value === this._y.value) {
            return `scale(${this._x})`;
        }
        if (this._y.value === 1) {
            return `scaleX(${this._x})`;
        }
        if (this._x.value === 1) {
            return `scaleY(${this._y})`;
        }
        return `scale(${this._x}, ${this._y})`;
    }
}
/**
 * @public
 */
(function (Scale) {
    function isScale(value) {
        return value instanceof Scale;
    }
    Scale.isScale = isScale;
    /**
     * {@link https://drafts.csswg.org/css-transforms/#funcdef-transform-scale}
     */
    const parseScaleFunc = map(CSSFunction.parse("scale", map(List.parseCommaSeparated(either(Number.parse, Percentage.parse), 1, 2), (list) => list.values)), ([_, [x, y]]) => Scale.of(x, y ?? x));
    /**
     * {@link https://drafts.csswg.org/css-transforms-2/#funcdef-scale3d}
     */
    const parseScale3dFunc = map(CSSFunction.parse("scale3d", map(List.parseCommaSeparated(either(Number.parse, Percentage.parse), 3, 3), (list) => list.values)), ([_, [x, y, z]]) => Scale.of(x, y, z));
    /**
     * {@link https://drafts.csswg.org/css-transforms/#funcdef-transform-scalex}
     */
    const parseScaleXFunc = map(CSSFunction.parse("scaleX", either(Number.parse, Percentage.parse)), ([_, x]) => Scale.of(x, Number.of(1)));
    /**
     * {@link https://drafts.csswg.org/css-transforms/#funcdef-transform-scaley}
     */
    const parseScaleYFunc = map(CSSFunction.parse("scaleY", either(Number.parse, Percentage.parse)), ([_, y]) => Scale.of(Number.of(1), y));
    /**
     * {@link https://drafts.csswg.org/css-transforms-2/#funcdef-scalez}
     */
    const parseScaleZFunc = map(CSSFunction.parse("scaleZ", either(Number.parse, Percentage.parse)), ([_, z]) => Scale.of(Number.of(1), Number.of(1), z));
    Scale.parse = either(parseScaleFunc, parseScaleXFunc, parseScaleYFunc, parseScale3dFunc, parseScaleZFunc);
    /**
     * {@link https://developer.mozilla.org/en-US/docs/Web/CSS/scale}
     */
    Scale.parseProp = map(List.parseSpaceSeparated(either(Number.parse, Percentage.parse), 1, 3), (list) => {
        const [x, y, z] = list.values;
        return Scale.of(x, y ?? x, z);
    });
})(Scale || (Scale = {}));
//# sourceMappingURL=scale.js.map